<template>
  <div class="ophthalmology-page">
    <datalist id="visual-acuity-suggestions">
      <option
        v-for="(value, idx) in visualAcuityValues"
        :value="value"
        :key="idx"
      />
    </datalist>

    <div class="columns">
      <div
        v-if="!triage"
        class="column col-6 col-xl-12 form-group"
        :class="{'has-error': $v.input_mainComplaint.$error}"
      >
        <label class="form-label text-bold">Queixa principal</label>
        <st-editor
          id="main-complaint"
          v-model="input_mainComplaint"
          :disabled="disabled"
          @blur="$v.input_mainComplaint.$touch()"
        />

        <template v-if="$v.input_mainComplaint.$error">
          <div class="form-input-hint"
               v-if="!$v.input_mainComplaint.required">
            Campo obrigatório
          </div>
        </template>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('mainComplaint', 'Queixa principal')"
        >Exibir histórico</button>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2 ml-2"
          @click="getLastValue('mainComplaint')"
        >Anotação prévia</button>
      </div>
      <div v-if="!triage" class="column col-6 col-xl-12 form-group">
        <label class="form-label text-bold">História pregressa / familiar</label>
        <st-editor
          id="medical-history"
          v-model="input_medicalHistory"
          :disabled="disabled"
        />
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('medicalHistory', 'História pregressa')"
        >Exibir histórico</button>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2 ml-2"
          @click="getLastValue('medicalHistory')"
        >Anotação prévia</button>
      </div>

      <!-- AV SC / AV CC -->
      <div v-if="!triage" class="column col-3 col-xl-6 col-md-12 form-group">
        <div class="group-items visual-acuity">
          <div class="columns">
            <div class="column col-6 form-group mb-0">
              <label class="form-label text-bold">AV SC</label>
              <div class="items">
                <span class="side">D</span>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="input_avscR"
                  list="visual-acuity-suggestions"
                  placeholder="AV SC"
                >
              </div>
              <div class="items">
                <span class="side">E</span>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="input_avscL"
                  list="visual-acuity-suggestions"
                  placeholder="AV SC"
                >
              </div>
            </div>
            <div class="column col-6 form-group mb-0">
              <label class="form-label text-bold">AV CC</label>
              <div class="items">
                <span class="side">D</span>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="input_avccR"
                  list="visual-acuity-suggestions"
                  placeholder="AV CC"
                >
              </div>
              <div class="items">
                <span class="side">E</span>
                <input
                  type="text"
                  class="form-input text-center"
                  v-model="input_avccL"
                  list="visual-acuity-suggestions"
                  placeholder="AV CC"
                >
              </div>
            </div>
          </div>
          <div class="items items-actions mb-1">
            <span class="side"></span>
            <button class="btn btn-sm btn-gray-outline btn-icon btn-icon-left mb-1"
                    @click="openHistory('visualAcuity')"
            ><fa-icon :icon="['fal', 'history']" /> Histórico</button>
            <button
              type="button"
              class="btn btn-sm btn-gray-outline ml-2 mb-1"
              @click="getLastValue('avsc|avcc')"
            >Anotação prévia</button>
          </div>
        </div>
      </div>

      <!-- Auto PIO / PIO -->
      <div class="column col-4 col-xl-6 col-md-12 form-group">
        <div class="group-items tonometry">
          <div class="columns">
            <div class="column form-group" :class="triage ? 'col-12' : 'col-6 mb-0'">
              <label class="form-label text-bold">Auto PIO</label>
              <div class="items">
                <span class="side">D</span>
                <div
                  class="input-group"
                  :class="{ tooltip: input_autoTonometryTimeR }"
                  :data-tooltip="input_autoTonometryTimeR"
                >
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="80"
                         v-model="input_autoTonometryR">
                  <span class="input-group-addon">mmHg</span>
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div
                  class="input-group"
                  :class="{ tooltip: input_autoTonometryTimeL }"
                  :data-tooltip="input_autoTonometryTimeL"
                >
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="80"
                         v-model="input_autoTonometryL">
                  <span class="input-group-addon">mmHg</span>
                </div>
              </div>
            </div>
            <div v-if="!triage" class="column col-6 form-group mb-0">
              <label class="form-label text-bold">PIO</label>
              <div class="items mb-1">
                <span class="side">D</span>
                <div
                  class="input-group"
                  :class="{ tooltip: input_tonometryTimeR }"
                  :data-tooltip="input_tonometryTimeR"
                >
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="80"
                         v-model="input_tonometryR">
                  <span class="input-group-addon">mmHg</span>
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div
                  class="input-group"
                  :class="{ tooltip: input_tonometryTimeL }"
                  :data-tooltip="input_tonometryTimeL"
                >
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="80"
                         v-model="input_tonometryL">
                  <span class="input-group-addon">mmHg</span>
                </div>
              </div>
            </div>
          </div>
          <div v-if="!triage" class="items items-actions mb-1">
            <span class="side"></span>
            <button
              class="btn btn-sm btn-gray-outline btn-icon btn-icon-left mb-1"
              @click="openHistory('tonometry')"
            ><fa-icon :icon="['fal', 'history']" /> Exibir Histórico</button>
          </div>
        </div>
      </div>

      <!-- Lensometria -->
      <div v-if="!triage" class="column col-5 col-xl-12 form-group">
        <div class="group-items lensometry">
          <div class="columns">
            <div class="column col-12 form-group">
              <label class="form-label text-bold">Lensometria</label>
              <div class="items">
                <span class="side">D</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_lensometrySphericalR
                         && input_lensometrySphericalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_lensometrySphericalR"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_lensometryCylindricalR
                         && input_lensometryCylindricalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         v-model="input_lensometryCylindricalR"
                         @blur="$v.input_lensometryAxisR.$touch()"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_lensometryAxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_lensometryAxisR"
                         placeholder="Eixo">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.25" min="0.50" max="6" placeholder="Adição"
                         v-model="input_lensometryAddition">
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_lensometrySphericalL
                         && input_lensometrySphericalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_lensometrySphericalL"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_lensometryCylindricalL
                         && input_lensometryCylindricalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         v-model="input_lensometryCylindricalL"
                         @blur="$v.input_lensometryAxisL.$touch()"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_lensometryAxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_lensometryAxisL"
                         placeholder="Eixo">
                </div>
                <div>
                  <select class="form-select text-center"
                          v-model="input_lensometryQuantity">
                    <option value="null">Qtde.</option>
                    <option v-for="(item, i) in qty" :value="item"
                            :key="i">{{ item }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="items">
                <span class="side"></span>
                <div>
                  <input type="text" class="form-input"
                         v-model="input_lensometryNotes"
                         placeholder="Observações...">
                </div>
                <div style="flex: 0; display: flex; gap: 4px;">
                  <button
                    class="btn btn btn-gray-outline btn-icon tooltip"
                    @click="openHistory('lensometry', null, historyGroups.set1)"
                    data-tooltip="Exibir histórico"
                  ><fa-icon :icon="['fal', 'history']" /></button>
                  <button
                    class="btn btn btn-gray-outline btn-icon tooltip"
                    @click="copyAutoRefractionToLensometry"
                    data-tooltip="Importar última Refração Subjetiva"
                  ><fa-icon :icon="['fal', 'copy']" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Auto Refração / Ceratometria -->
      <div class="column col-12 form-group">
        <div class="group-items auto-refraction">
          <div class="columns">
            <div class="column col-6 col-md-12 form-group">
              <label class="form-label text-bold">Auto Refração</label>
              <div class="items">
                <span class="side">D</span>
                <div>
                  <input type="number" class="form-input text-center"
                         :class="input_autoRefSphericalR
                         && input_autoRefSphericalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_autoRefSphericalR"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         :class="input_autoRefCylindricalR
                         && input_autoRefCylindricalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         v-model="input_autoRefCylindricalR"
                         @blur="$v.input_autoRefAxisR.$touch()"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_autoRefAxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_autoRefAxisR"
                         placeholder="Eixo">
                </div>
                <div>
                  <label
                    for="pupillary-distance"
                    class="form-input bg-gray text-center"
                  >D.P.</label>
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_autoRefSphericalL
                         && input_autoRefSphericalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_autoRefSphericalL"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_autoRefCylindricalL
                         && input_autoRefCylindricalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         @blur="$v.input_autoRefAxisL.$touch()"
                         v-model="input_autoRefCylindricalL"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_autoRefAxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_autoRefAxisL"
                         placeholder="Eixo">
                </div>
                <div>
                  <div class="input-group">
                    <input
                      type="number"
                      class="form-input"
                      id="pupillary-distance"
                      placeholder="50 - 80"
                      v-model="input_pupillaryDistance"
                      min="50"
                      max="80"
                      step="0.1"
                    >
                    <span class="input-group-addon">mm</span>
                  </div>
                </div>
              </div>
              <div class="items">
                <span class="side"></span>
                <div>
                  <input type="text" class="form-input"
                         v-model="input_autoRefNotes"
                         placeholder="Observações...">
                </div>
                <div v-if="!triage" style="flex: 0">
                  <button
                    class="btn btn-gray-outline btn-icon tooltip"
                    @click="openHistory('autoRefraction', null, historyGroups.set1)"
                    data-tooltip="Exibir Histórico"
                  ><fa-icon :icon="['fal', 'history']" /></button>
                </div>
              </div>
            </div>
            <div class="column col-6 col-md-12 form-group">
              <label class="form-label text-bold">Ceratometria</label>
              <div class="items">
                <span class="side">D</span>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryK1R"
                         @blur="$v.input_keratometryK1AxisR.$touch()"
                         placeholder="K1">
                </div>
                <div :class="{'has-error': $v.input_keratometryK1AxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_keratometryK1AxisR"
                         placeholder="Eixo K1">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryK2R"
                         @blur="$v.input_keratometryK2AxisR.$touch()"
                         placeholder="K2">
                </div>
                <div :class="{'has-error': $v.input_keratometryK2AxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_keratometryK2AxisR"
                         placeholder="Eixo K2">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryCylinderR"
                         placeholder="Cil.">
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryK1L"
                         @blur="$v.input_keratometryK1AxisL.$touch()"
                         placeholder="K1">
                </div>
                <div :class="{'has-error': $v.input_keratometryK1AxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_keratometryK1AxisL"
                         placeholder="Eixo K1">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryK2L"
                         @blur="$v.input_keratometryK2AxisL.$touch()"
                         placeholder="K2">
                </div>
                <div :class="{'has-error': $v.input_keratometryK2AxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_keratometryK2AxisL"
                         placeholder="Eixo K2">
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.01" min="20" max="80"
                         v-model="input_keratometryCylinderL"
                         placeholder="Cil.">
                </div>
              </div>
              <div class="items">
                <span class="side"></span>
                <div>
                  <input type="text" class="form-input"
                         v-model="input_keratometryNotes"
                         placeholder="Observações...">
                </div>
                <div v-if="!triage" style="flex: 0">
                  <button
                    class="btn btn-icon btn-gray-outline tooltip"
                    @click="openHistory('keratometry', null, historyGroups.set1)"
                    data-tooltip="Exibir Histórico"
                  ><fa-icon :icon="['fal', 'history']" /></button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Refração Subjetiva / Refração Estática -->
      <div v-if="!triage" class="column col-12 form-group">
        <div class="group-items refraction">
          <div class="columns">
            <div class="column col-6 col-md-12 col-sm-12 form-group mb-1">
              <label class="form-label text-bold">Refração Subjetiva</label>
              <div class="items">
                <span class="side">D</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refSubjSphericalR
                         && input_refSubjSphericalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_refSubjSphericalR"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refSubjCylindricalR
                         && input_refSubjCylindricalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         @blur="$v.input_refSubjAxisR.$touch()"
                         v-model="input_refSubjCylindricalR"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_refSubjAxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_refSubjAxisR"
                         placeholder="Eixo">
                </div>
                <div>
                  <input
                    type="text"
                    class="form-input text-center"
                    v-model="input_refSubjAvccR"
                    list="visual-acuity-suggestions"
                    placeholder="AV CC"
                  >
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.25" min="0.50" max="6" placeholder="Adição"
                         v-model="input_refSubjAddition">
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refSubjSphericalL
                         && input_refSubjSphericalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_refSubjSphericalL"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refSubjCylindricalL
                         && input_refSubjCylindricalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         @blur="$v.input_refSubjAxisL.$touch()"
                         v-model="input_refSubjCylindricalL"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_refSubjAxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_refSubjAxisL"
                         placeholder="Eixo">
                </div>
                <div>
                  <input
                    type="text"
                    class="form-input text-center"
                    v-model="input_refSubjAvccL"
                    list="visual-acuity-suggestions"
                    placeholder="AV CC"
                  >
                </div>
                <div>
                  <select class="form-select text-center"
                          v-model="input_refSubjQuantity">
                    <option value="null">Qtde.</option>
                    <option v-for="(item, i) in qty" :value="item" :key="i">{{ item }}</option>
                  </select>
                </div>
              </div>
              <div class="items">
                <span class="side"></span>
                <div>
                  <input
                    type="text"
                    class="form-input"
                    v-model="input_refSubjNotes"
                    placeholder="Observações..."
                  >
                </div>
              </div>
              <div class="items items-actions">
                <span class="side"></span>
                <div class="action-group">
                  <st-button-report
                    type="pdf"
                    label="Imprimir Receita"
                    button-class="btn-sm btn-gray"
                    :route="recipePrintLink('subjective')"
                    :options="[]"
                  ></st-button-report>
                  <button
                    type="button"
                    class="btn btn-sm btn-gray-outline btn-icon btn-icon-left ml-2"
                    @click="openHistory('subjective', null, historyGroups.set2)"
                  ><fa-icon :icon="['fal', 'history']" /> Exibir Histórico</button>
                  <button
                    type="button"
                    class="btn btn-sm btn-gray-outline ml-2"
                    @click="getLastValue('refSub')"
                  >Anotação prévia</button>
                </div>
              </div>
            </div>
            <div class="column col-6 col-md-12 col-sm-12 form-group mb-1">
              <label class="form-label text-bold">Refração Estática</label>
              <div class="items">
                <span class="side">D</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refObjSphericalR
                         && input_refObjSphericalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_refObjSphericalR"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refObjCylindricalR
                         && input_refObjCylindricalR.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         @blur="$v.input_refObjAxisR.$touch()"
                         v-model="input_refObjCylindricalR"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_refObjAxisR.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_refObjAxisR"
                         placeholder="Eixo">
                </div>
                <div>
                  <input
                    type="text"
                    class="form-input text-center"
                    v-model="input_refObjAvccR"
                    list="visual-acuity-suggestions"
                    placeholder="AV CC"
                  >
                </div>
                <div>
                  <input type="number" class="form-input text-center"
                         step="0.25" min="0.50" max="6" placeholder="Adição"
                         v-model="input_refObjAddition">
                </div>
              </div>
              <div class="items">
                <span class="side">E</span>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refObjSphericalL
                         && input_refObjSphericalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-35" max="30"
                         v-model="input_refObjSphericalL"
                         placeholder="Esf.">
                </div>
                <div>
                  <input type="number" class="form-input text-center mr-1"
                         :class="input_refObjCylindricalL
                         && input_refObjCylindricalL.toString().startsWith('-')
                         ? 'negative-value' : ''"
                         step="0.25" min="-30" max="0"
                         @blur="$v.input_refObjAxisL.$touch()"
                         v-model="input_refObjCylindricalL"
                         placeholder="Cil.">
                </div>
                <div :class="{'has-error': $v.input_refObjAxisL.$error}">
                  <input type="number" class="form-input text-center"
                         step="1" min="0" max="180"
                         v-model="input_refObjAxisL"
                         placeholder="Eixo">
                </div>
                <div>
                  <input
                    type="text"
                    class="form-input text-center"
                    v-model="input_refObjAvccL"
                    list="visual-acuity-suggestions"
                    placeholder="AV CC"
                  >
                </div>
                <div>
                  <select class="form-select text-center"
                          v-model="input_refObjQuantity">
                    <option value="null">Qtde.</option>
                    <option v-for="(item, i) in qty" :value="item" :key="i">{{ item }}</option>
                  </select>
                </div>
              </div>
              <div class="items">
                <span class="side"></span>
                <div>
                  <input
                    type="text"
                    class="form-input"
                    v-model="input_refObjNotes"
                    placeholder="Observações..."
                  >
                </div>
              </div>
              <div class="items items-actions">
                <span class="side"></span>
                <div class="action-group">
                  <st-button-report
                    type="pdf"
                    label="Imprimir Receita"
                    button-class="btn-sm btn-gray"
                    :route="recipePrintLink('objective')"
                    :options="[]"
                  ></st-button-report>
                  <button
                    type="button"
                    class="btn btn-sm btn-gray-outline btn-icon btn-icon-left ml-2"
                    @click="openHistory('objective', null, historyGroups.set2)"
                  ><fa-icon :icon="['fal', 'history']" /> Exibir Histórico</button>
                  <button
                    type="button"
                    class="btn btn-sm btn-gray-outline ml-2"
                    @click="getLastValue('refObj')"
                  >Anotação prévia</button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Biomicroscopia</label>
        <div class="input-group">
          <span class="input-group-addon">OD</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_biomicroscopyR"
          />
        </div>
        <div class="input-group mt-2">
          <span class="input-group-addon">OE</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_biomicroscopyL"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('biomicroscopy(R|L)','Biomicroscopia')"
        >Exibir histórico</button>
      </div>
      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Fundo de Olho</label>
        <div class="input-group">
          <span class="input-group-addon">OD</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_fundusR"
          />
        </div>
        <div class="input-group mt-2">
          <span class="input-group-addon">OE</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_fundusL"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('fundus(R|L)','Fundo de Olho')"
        >Exibir histórico</button>
      </div>

      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Lente de contato</label>
        <div class="input-group">
          <span class="input-group-addon">OD</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_contactLensR"
          />
        </div>
        <div class="input-group mt-2">
          <span class="input-group-addon">OE</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_contactLensL"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('contactLens(R|L)','Lente de contato')"
        >Exibir histórico</button>
      </div>
      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Gonioscopia</label>
        <div class="input-group">
          <span class="input-group-addon">OD</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_gonioscopyR"
          />
        </div>
        <div class="input-group mt-2">
          <span class="input-group-addon">OE</span>
          <textarea
            class="form-input auto-sizing"
            v-model="input_gonioscopyL"
          />
        </div>
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('gonioscopy(R|L)','Gonioscopia')"
        >Exibir histórico</button>
      </div>

      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Conduta</label>
        <st-editor
          id="conduct"
          v-model="input_conduct"
          :disabled="disabled"
        />
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('conduct', 'Conduta')"
        >Exibir histórico</button>
      </div>
      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Exames complementares</label>
        <st-editor
          id="complementary-exams"
          v-model="input_complementaryExams"
          :disabled="disabled"
        />
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('complementaryExams', 'Exames complementares')"
        >Exibir histórico</button>
      </div>

      <div v-if="!triage" class="column col-6 col-md-12 form-group">
        <label class="form-label text-bold">Diagnóstico</label>
        <input type="text" class="form-input"
               v-model="input_diagnosis">
        <button
          type="button"
          class="btn btn-sm btn-gray-outline mt-2"
          @click="openHistory('diagnosis', 'Diagnóstico')"
        >Exibir histórico</button>
      </div>

      <div v-if="!triage" class="column col-6 col-md-12 form-group mb-2">
        <label class="form-label text-bold">CID</label>
        <dx-autocomplete
          id="cid"
          v-model="cid"
          :source="findCid"
          @select="addCid"
          label="code"
          :debounce="800"
          placeholder="Pesquisar CID"
        >
          <template v-slot="{ item }">
            <fa-icon class="text-primary" :icon="['fal', 'heartbeat']"/>
            {{ item.code }} - {{ item.name }}
          </template>
          <template v-slot:action>
            <button
              class="btn btn-neutral btn-action input-group-btn btn-icon"
            ><fa-icon :icon="['fal', 'search']"/></button>
          </template>
        </dx-autocomplete>
        <div
          class="chip chip-cid mt-2"
          v-for="(cid, cidIdx) in cids"
          :key="cid.code"
        >
          <span>{{ cid.code }}</span>
          <span class="ml-1 mr-1 text-gray">-</span>
          <span class="text-ellipsis">{{ cid.name }}</span>
          <a
            v-if="!disabled"
            href="#"
            class="text-large ml-2"
            aria-label="Close"
            role="button"
            @click="removeCid(cidIdx)"
          ><fa-icon :icon="['fal', 'times']" /></a>
        </div>
      </div>

      <div class="column col-12 form-group">
        <label class="form-label text-bold">Observações</label>
        <template v-if="!triage">
          <textarea
            class="form-input auto-sizing"
            v-model="input_observations"
          />
          <button
            class="btn btn-sm btn-gray-outline mt-2"
            @click="openHistory('observations', 'Observações')"
          >Exibir Histórico</button>
        </template>
        <template v-else>
          <textarea
            class="form-input auto-sizing"
            placeholder="Observações da triagem..."
          />
        </template>
      </div>
    </div>

    <HistoryModal
      v-if="historyModal.show"
      :show="historyModal.show"
      :history="history"
      :selector="historyModal.selector"
      :label="historyModal.label"
      :groups="historyModal.groups"
      @input="closeHistory"
      @select="insertHistoryValue"
    />
  </div>
</template>

<script>
import { round } from 'lodash';
import moment from 'moment';
import { required } from 'vuelidate/lib/validators';
import { isHTML } from '@/helpers/html';
import formMixin from '@/mixins/form';
import { inputs as dataInputs, visualAcuityValues } from '../ophtalmology/data';
import HistoryModal from '../ophtalmology/HistoryModal.vue';
import { useInputs } from '../helpers/inputs';

function mapPropsModels() {
  return dataInputs.reduce((obj, { name }) => {
    obj[`input_${name}`] = {
      get() {
        return this.form.inputs.find(item => item.name === name)?.value || null;
      },
      set(value) {
        this.$emit('change', { name, value });
      },
    };
    return obj;
  }, {});
}

export default {
  mixins: [formMixin],
  components: {
    HistoryModal,
  },
  props: {
    data: {
      type: Object,
    },
    id: {
      type: String,
    },
    history: {
      type: Array,
      default: () => [],
    },
    cids: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  inject: ['triage'],
  data() {
    return {
      form: this.data,
      visualAcuityValues,
      qty: [
        'J1',
        'J2',
        'J3',
        'J4',
        'J5',
        'J6',
        '>J6',
      ],
      historyModal: {
        show: false,
        selector: '',
        label: '',
      },
      cid: null,
      historyGroups: {
        set1: {
          autoRefraction: 'Auto Refração',
          keratometry: 'Ceratometria',
          lensometry: 'Lensometria',
        },
        set2: {
          subjective: 'Refração Subjetiva',
          objective: 'Refração Estática',
        },
      },
      htmlInputs: [
        'mainComplaint',
        'medicalHistory',
        'conduct',
        'complementaryExams',
      ],
    };
  },
  computed: {
    ...mapPropsModels(),
  },
  validations() {
    const rules = {
      input_mainComplaint: { required },
      input_autoRefAxisR: {},
      input_autoRefAxisL: {},
      input_keratometryK1AxisR: {},
      input_keratometryK1AxisL: {},
      input_keratometryK2AxisR: {},
      input_keratometryK2AxisL: {},
      input_lensometryAxisR: {},
      input_lensometryAxisL: {},
      input_refSubjAxisR: {},
      input_refSubjAxisL: {},
      input_refObjAxisR: {},
      input_refObjAxisL: {},
    };

    if (this.input_autoRefCylindricalR) {
      rules.input_autoRefAxisR = { required };
    }

    if (this.input_autoRefCylindricalL) {
      rules.input_autoRefAxisL = { required };
    }

    if (this.input_keratometryK1R) {
      rules.input_keratometryK1AxisR = { required };
    }

    if (this.input_keratometryK1L) {
      rules.input_keratometryK1AxisL = { required };
    }

    if (this.input_keratometryK2R) {
      rules.input_keratometryK2AxisR = { required };
    }

    if (this.input_keratometryK2L) {
      rules.input_keratometryK2AxisL = { required };
    }

    if (this.input_lensometryCylindricalR) {
      rules.input_lensometryAxisR = { required };
    }

    if (this.input_lensometryCylindricalL) {
      rules.input_lensometryAxisL = { required };
    }

    if (this.input_refSubjCylindricalR) {
      rules.input_refSubjAxisR = { required };
    }

    if (this.input_refSubjCylindricalL) {
      rules.input_refSubjAxisL = { required };
    }

    if (this.input_refObjCylindricalR) {
      rules.input_refObjAxisR = { required };
    }

    if (this.input_refObjCylindricalL) {
      rules.input_refObjAxisL = { required };
    }

    return rules;
  },
  mounted() {
    this.touchItems();
  },
  watch: {
    input_autoTonometryR(value) {
      this.updateTonometryTimestamp('autoTonometryTimeR', value);
    },
    input_autoTonometryL(value) {
      this.updateTonometryTimestamp('autoTonometryTimeL', value);
    },
    input_tonometryR(value) {
      this.updateTonometryTimestamp('tonometryTimeR', value);
    },
    input_tonometryL(value) {
      this.updateTonometryTimestamp('tonometryTimeL', value);
    },
    input_keratometryK1AxisR(value) {
      this.updateKeratometryK2Axis('R', value);
    },
    input_keratometryK1AxisL(value) {
      this.updateKeratometryK2Axis('L', value);
    },
    input_keratometryK1R() {
      this.updateKeratometryCylinder('R');
    },
    input_keratometryK2R() {
      this.updateKeratometryCylinder('R');
    },
    input_keratometryK1L() {
      this.updateKeratometryCylinder('L');
    },
    input_keratometryK2L() {
      this.updateKeratometryCylinder('L');
    },
  },
  methods: {
    updateTonometryTimestamp(field, hasValue) {
      this[`input_${field}`] = hasValue ? moment().format('HH:mm') : '';
    },
    calculateKeratometryK2Axis(k1AxisValue) {
      const normalizedAxis = k1AxisValue % 180;
      return (normalizedAxis + 90) % 180;
    },
    updateKeratometryK2Axis(side, value) {
      this[`keratometryK2Axis${side}`] = this.calculateKeratometryK2Axis(value);
    },
    calculateKeratometryCylinder(k1Value, k2Value) {
      return k1Value && k2Value ? round(k1Value - k2Value, 2) : null;
    },
    updateKeratometryCylinder(side) {
      const value = this.calculateKeratometryCylinder(
        this[`input_keratometryK1${side}`],
        this[`input_keratometryK2${side}`],
      );
      if (value !== null) {
        this[`input_keratometryCylinder${side}`] = value;
      }
    },
    touchItems() {
      this.$v.input_autoRefAxisR.$touch();
      this.$v.input_autoRefAxisL.$touch();
      this.$v.input_keratometryK1AxisR.$touch();
      this.$v.input_keratometryK1AxisL.$touch();
      this.$v.input_keratometryK2AxisR.$touch();
      this.$v.input_keratometryK2AxisL.$touch();
      this.$v.input_lensometryAxisR.$touch();
      this.$v.input_lensometryAxisL.$touch();
      this.$v.input_refSubjAxisR.$touch();
      this.$v.input_refSubjAxisL.$touch();
      this.$v.input_refObjAxisR.$touch();
      this.$v.input_refObjAxisL.$touch();
    },
    recipePrintLink(type) {
      return `/medical-records/${this.id}/print-ophthalmology-recipes?type=${type}`;
    },
    insertHistoryValue({ inputs }) {
      Object.keys(inputs).forEach((key) => {
        let value = inputs[key];
        if (this.htmlInputs.includes(key) && !isHTML(value)) {
          value = value.replace(/(\r\n|\n|\r)/gm, '<br>');
        }
        this[`input_${key}`] = value;
      });
    },
    getLastValue(selector) {
      const found = this.history
        .find(({ anamnesis }) => {
          const inputHelper = useInputs(anamnesis.inputs || {});
          return inputHelper.hasFilledWith(selector);
        });

      if (found) {
        const inputHelper = useInputs(found.anamnesis.inputs);
        const inputs = inputHelper.getWith(selector);
        this.insertHistoryValue({ inputs });
      }
    },
    openHistory(selector, label, groups = null) {
      this.historyModal.selector = selector;
      this.historyModal.label = label;
      this.historyModal.groups = groups;
      this.historyModal.show = true;
    },
    closeHistory() {
      this.historyModal.show = false;
    },
    addCid(item) {
      if (this.disabled) {
        return;
      }
      this.$emit('add-cid', item);
      this.cid = null;
    },
    removeCid(idx) {
      if (this.disabled) {
        return;
      }
      this.$emit('remove-cid', idx);
    },
    findCid(search) {
      return this.$httpX.get(`/services/cids?search=${search}`)
        .then(({ data }) => {
          if (data.items.length === 0) {
            return [];
          }
          return data.items;
        });
    },
    copyAutoRefractionToLensometry() {
      const found = this.history
        .find(({ anamnesis }) => {
          const inputHelper = useInputs(anamnesis.inputs || {});
          return inputHelper.hasFilledWith('refSubj');
        });

      if (found) {
        const inputHelper = useInputs(found.anamnesis.inputs);
        const inputs = inputHelper.getWith('refSubj');

        if (inputs.refSubjSphericalR) {
          this.input_lensometrySphericalR = inputs.refSubjSphericalR;
        }
        if (inputs.refSubjCylindricalR) {
          this.input_lensometryCylindricalR = inputs.refSubjCylindricalR;
        }
        if (inputs.refSubjAxisR) this.input_lensometryAxisR = inputs.refSubjAxisR;
        if (inputs.refSubjSphericalL) {
          this.input_lensometrySphericalL = inputs.refSubjSphericalL;
        }
        if (inputs.refSubjCylindricalL) {
          this.input_lensometryCylindricalL = inputs.refSubjCylindricalL;
        }
        if (inputs.refSubjAxisL) this.input_lensometryAxisL = inputs.refSubjAxisL;
        if (inputs.refSubjNotes) this.input_lensometryNotes = inputs.refSubjNotes;
      }
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables.scss";
@import "@/assets/scss/_colors.scss";

.ophthalmology-page {
  .form-input {
    opacity: 100;
  }
  .negative-value {
    color: red;
  }
  .group-items {
    border: $border-width-lg solid $gray-color-400;
    border-radius: $border-radius;
    margin-top: $layout-spacing-sm;
    padding: $layout-spacing-sm $layout-spacing;
    .form-label {
      margin-left: $font-size;
    }
    .items {
      align-items: center;
      display: flex;
      margin-bottom: $layout-spacing-sm;
      .side {
        color: $gray-color;
        min-width: $font-size;
      }
      & > div {
        flex: 1;
        margin-right: $layout-spacing-sm;
        &:last-child {
          margin-right: 0;
        }
      }
      &:last-child {
        margin-bottom: 0;
      }
    }
    .items-actions {
      margin-top: $layout-spacing;
      .action-group {
        display: flex;
        align-items: flex-start;
      }
    }
    &.auto-refraction {
      border-color: $dark-blue-color-300;
    }
    &.visual-acuity {
      border-color: $purple-color-300;
    }
    &.tonometry {
      border-color: $orange-color-300;
    }
    &.lensometry {
      border-color: $blue-color-300;
    }
    &.refraction {
      border-color: $green-color-300;
    }

    .form-group:last-child {
      margin-bottom: $layout-spacing;
    }
  }

  .document-editor {
    .document-editor-body .ck.ck-content.ck-editor__editable {
      min-height: 5.5rem;
      padding: $layout-spacing $layout-spacing-lg;
      p:first-child {
        margin-top: $border-width-lg;
      }
      p:last-child {
        margin-bottom: $border-width-lg;
      }
    }
  }

  .chip-cid {
    border: $border-width solid $info-color;
    background-color: $gray-color-ultra-light;
    color: $secondary-color-dark;
    a {
      color: $gray-color-dark;
      &:hover {
        color: $error-color;
      }
    }
  }
}

#medical-record-history-modal {
  .table-history {
    td {
      vertical-align: top;
    }
  }
}
</style>
